import React, { useState, useMemo, useEffect } from 'react';
import { quizSteps } from '../data/quizSteps';
import { ArrowLeft, Check, Users, Calendar, ThumbsUp, ThumbsDown, Star, Shield, Sparkles, Award } from 'lucide-react';
import axios from 'axios';
import BookingStep from './BookingStep';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '573001234567';

// Before/After real photos
const BEFORE_IMG = 'https://69f12f021c78705e2c4a6757.imgix.net/Antes%201.webp';
const AFTER_IMG = 'https://69f12f021c78705e2c4a6757.imgix.net/Despues%201%20p.webp';
const RESULT_IMG = 'https://images.pexels.com/photos/6975474/pexels-photo-6975474.jpeg?auto=compress&cs=tinysrgb&w=800';

export default function Quiz({ onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState({
    datos: { edad: 32, peso: 65, talla: 163 },
    tecnologias: [],
  });
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const step = quizSteps[stepIdx];
  const total = quizSteps.length;
  const progress = ((stepIdx + 1) / total) * 100;

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [stepIdx]);

  const setAnswer = (field, value) => setAnswers((a) => ({ ...a, [field]: value }));
  const toggleMulti = (field, value) => {
    setAnswers((a) => {
      const cur = a[field] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...a, [field]: next };
    });
  };

  const next = () => { setDirection(1); setStepIdx((i) => Math.min(i + 1, total - 1)); };
  const back = () => {
    if (stepIdx === 0) onClose?.();
    else { setDirection(-1); setStepIdx((i) => i - 1); }
  };

  const canContinue = useMemo(() => {
    if (!step) return false;
    if (step.type === 'testimonial' || step.type === 'insight' || step.type === 'yesno' || step.type === 'sliders' || step.type === 'booking') return true;
    if (step.type === 'multi') return (answers[step.field] || []).length > 0;
    if (step.type === 'options') return !!answers[step.field];
    if (step.type === 'contact') return contact.name.trim().length > 1 && contact.phone.trim().length >= 7;
    return true;
  }, [step, answers, contact]);

  const submitLead = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { name: contact.name, phone: contact.phone, email: contact.email, answers });
      setSubmitted(true);
      const msg = encodeURIComponent(`Hola Mistika, soy ${contact.name}. Acabo de completar la valoración y quiero agendar mi cita gratuita. Tel: ${contact.phone}`);
      setTimeout(() => { window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank'); }, 800);
    } catch (e) {
      alert('Hubo un error. Intenta de nuevo o escríbenos por WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  const [bookingResult, setBookingResult] = useState(null);
  const onBookingSuccess = (res) => { setBookingResult(res); setSubmitted(true); };

  if (submitted) {
    return (
      <div className="hero-bg stars min-h-screen flex items-center justify-center px-4 py-10">
        <div className="quiz-dark relative z-10 w-full max-w-xl p-8 sm:p-10 float-in" data-testid="confirmation-card">
          <div className="text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 mx-auto flex items-center justify-center mb-5 shadow-2xl shadow-fuchsia-500/60 neon-pulse">
              <Check className="w-12 h-12 text-white" strokeWidth={3} />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
              {bookingResult ? '¡Cita agendada!' : '¡Reservada con éxito!'}
            </h2>
            <p className="text-purple-200/80 mb-6">
              {bookingResult
                ? `Tu valoración está confirmada para el ${bookingResult.date} a las ${bookingResult.time} en ${bookingResult.city_name}. Una especialista te contactará pronto para confirmar.`
                : 'Te abriremos WhatsApp para confirmar tu cita gratuita. Una especialista te contactará en menos de 2 horas.'}
            </p>
          </div>
          <div className="urg-box-dark mb-6">
            <p className="leading-relaxed">
              <strong className="block mb-1">¿Qué sigue?</strong>
              {bookingResult ? (
                <>
                  · Revisa tu correo: te enviamos invitación al calendario<br />
                  · Recibirás recordatorio por WhatsApp 24h antes<br />
                  · Llega 10 min antes para tu análisis corporal
                </>
              ) : (
                <>
                  · Recibirás un mensaje por WhatsApp para confirmar<br />
                  · La valoración dura 45 min y es 100% gratuita<br />
                  · Tu especialista preparará tu plan personalizado
                </>
              )}
            </p>
          </div>
          {bookingResult?.event_link && (
            <a href={bookingResult.event_link} target="_blank" rel="noreferrer" className="block text-center text-fuchsia-300 hover:text-fuchsia-200 text-sm font-semibold mb-4 underline">
              Ver evento en Google Calendar →
            </a>
          )}
          <button onClick={onClose} className="cta-glow w-full" data-testid="back-home-button">Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-bg stars min-h-screen flex items-start justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-2xl">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-5">
          <button onClick={back} className="flex items-center gap-2 text-purple-200/80 hover:text-white text-sm font-semibold transition-colors" data-testid="quiz-back-button">
            <span className="w-9 h-9 rounded-full border border-purple-400/40 flex items-center justify-center hover:border-fuchsia-400 hover:bg-purple-500/20 transition">
              <ArrowLeft className="w-4 h-4" />
            </span>
            <span className="hidden sm:inline">{stepIdx === 0 ? 'Salir' : 'Atrás'}</span>
          </button>
          <p className="text-xs tracking-[0.25em] uppercase text-purple-300/80 font-bold">
            Paso <span className="text-fuchsia-300">{stepIdx + 1}</span> de {total}
          </p>
          <div className="w-9 h-9"></div>
        </div>

        {/* Progress steps dots */}
        <div className="flex items-center gap-1 mb-6">
          {quizSteps.map((_, i) => (
            <div key={i} className="flex-1 progress-track-dark" style={{ height: 5 }}>
              {i < stepIdx ? <div className="h-full bg-gradient-to-r from-purple-400 to-fuchsia-400 rounded-full" style={{ width: '100%' }} />
                : i === stepIdx ? <div className="progress-fill-dark" style={{ width: '70%' }} /> : null}
            </div>
          ))}
        </div>

        {/* Card */}
        <div key={stepIdx} className="quiz-dark p-6 sm:p-9 float-in" data-testid="quiz-card">
          <StepBody
            step={step}
            answers={answers}
            contact={contact}
            setContact={setContact}
            setAnswer={setAnswer}
            toggleMulti={toggleMulti}
            onBookingSuccess={onBookingSuccess}
          />

          <div className="mt-7">
            {step.type === 'yesno' ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setAnswer('interes', 'no'); next(); }}
                  className="quiz-option-dark flex items-center justify-center gap-2 font-semibold !py-3.5"
                  data-testid="yesno-no-button"
                >
                  <ThumbsDown className="w-4 h-4" /> No por ahora
                </button>
                <button
                  onClick={() => { setAnswer('interes', 'si'); next(); }}
                  className="cta-glow flex items-center justify-center gap-2 !py-3.5"
                  data-testid="yesno-yes-button"
                >
                  <ThumbsUp className="w-4 h-4" /> ¡Sí, quiero esto!
                </button>
              </div>
            ) : step.type === 'contact' ? (
              <button onClick={submitLead} disabled={!canContinue || submitting} className="cta-glow w-full" data-testid="submit-lead-button">
                {submitting ? 'Enviando…' : 'Agendar mi Valoración Gratuita →'}
              </button>
            ) : step.type === 'booking' ? null : (
              <button onClick={next} disabled={!canContinue} className="cta-glow w-full" data-testid="quiz-continue-button">
                {step.ctaLabel || 'Continuar →'}
              </button>
            )}
            {step.type === 'contact' && (
              <p className="text-center text-purple-300/60 text-xs mt-3">Sin compromiso · 100% confidencial</p>
            )}
          </div>
        </div>

        {/* Footer hint */}
        <div className="mt-6 flex items-center justify-center gap-2 text-purple-300/55 text-xs">
          <Shield className="w-3.5 h-3.5" /> Tu información está protegida. Nunca la compartimos.
        </div>
      </div>
    </div>
  );
}

const CAROUSEL_IMAGES = [
  'https://69f12f021c78705e2c4a6757.imgix.net/antes%20y%20despues%20web-sin%20peso.webp',
  'https://69f12f021c78705e2c4a6757.imgix.net/antes%20y%20despues%20web2.jpg',
  'https://69f12f021c78705e2c4a6757.imgix.net/antes%20y%20despues%20web-sin%20peso%203.webp',
];

function BeforeAfterCarousel() {
  const [idx, setIdx] = useState(0);
  const total = CAROUSEL_IMAGES.length;
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(t);
  }, [total]);
  const go = (delta) => setIdx((i) => (i + delta + total) % total);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-purple-400/30 h-56 sm:h-72 group" data-testid="ba-carousel">
      {CAROUSEL_IMAGES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`Antes y después ${i + 1}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === idx ? 1 : 0, filter: 'brightness(0.95) saturate(1.05)' }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-purple-950/85 via-transparent to-transparent pointer-events-none"></div>

      {/* Counter + tag */}
      <div className="absolute top-3 left-3 z-10 chip-dark !py-1 !px-2 !text-[10px] backdrop-blur-sm">
        {idx + 1} / {total}
      </div>

      {/* Prev / Next arrows */}
      <button
        type="button"
        onClick={() => go(-1)}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-purple-950/60 hover:bg-purple-900/80 backdrop-blur-sm border border-purple-400/40 text-white flex items-center justify-center z-10 transition opacity-70 hover:opacity-100"
        aria-label="Anterior"
        data-testid="carousel-prev"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-purple-950/60 hover:bg-purple-900/80 backdrop-blur-sm border border-purple-400/40 text-white flex items-center justify-center z-10 transition opacity-70 hover:opacity-100"
        aria-label="Siguiente"
        data-testid="carousel-next"
      >
        ›
      </button>

      {/* Caption */}
      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between z-10">
        <div>
          <p className="pill-tag mb-1.5"><Sparkles className="w-3 h-3" />CASO REAL</p>
          <p className="text-white text-sm font-semibold drop-shadow-lg">Resultado visible en 6 sesiones</p>
        </div>
        <Award className="w-7 h-7 text-fuchsia-300 neon-pulse" />
      </div>

      {/* Indicators */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {CAROUSEL_IMAGES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            className={`h-2 rounded-full transition-all border border-white/40 ${i === idx ? 'w-8 bg-fuchsia-400 shadow-lg shadow-fuchsia-400/60' : 'w-2 bg-white/30 hover:bg-white/70'}`}
            data-testid={`carousel-dot-${i}`}
            aria-label={`Imagen ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function StepBody({ step, answers, contact, setContact, setAnswer, toggleMulti, onBookingSuccess }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-purple-500/15 border border-purple-400/40 flex items-center justify-center neon-pulse">
          <Sparkles className="w-5 h-5 text-fuchsia-300" />
        </div>
        <p className="text-[11px] tracking-[0.3em] uppercase text-purple-300/80 font-bold">Valoración Mistika</p>
      </div>

      <h2 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight mb-2" data-testid="quiz-question-title">
        {step.title}
      </h2>
      {step.subtitle && <p className="text-purple-200/70 text-sm sm:text-base mb-5">{step.subtitle}</p>}

      {step.type === 'options' && (
        <div className="space-y-3" data-testid="quiz-options">
          {step.options.map((opt, k) => {
            const selected = answers[step.field] === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setAnswer(step.field, opt.value)}
                className={`quiz-option-dark w-full text-left flex items-start gap-3 slide-in ${selected ? 'selected' : ''}`}
                style={{ animationDelay: `${k * 60}ms` }}
                data-testid={`option-${step.field}-${opt.value}`}
              >
                <span className="quiz-check-dark"><Check className="w-3.5 h-3.5" strokeWidth={3.5} /></span>
                <span className="flex-1">
                  <span className="flex items-center gap-2">
                    {opt.emoji && <span className="text-lg">{opt.emoji}</span>}
                    <span className="font-bold text-white text-sm sm:text-base">{opt.label}</span>
                  </span>
                  {opt.desc && <span className="block text-purple-200/65 text-xs sm:text-sm mt-1">{opt.desc}</span>}
                </span>
              </button>
            );
          })}
          {step.urg && (
            <div className="urg-box-dark flex items-start gap-3 mt-4">
              {step.urg.icon === 'users' ? <Users className="w-5 h-5 mt-0.5 text-fuchsia-300 flex-shrink-0" /> : <Calendar className="w-5 h-5 mt-0.5 text-fuchsia-300 flex-shrink-0" />}
              <p className="leading-relaxed" dangerouslySetInnerHTML={{ __html: step.urg.html }} />
            </div>
          )}
        </div>
      )}

      {step.type === 'multi' && (
        <div className="grid sm:grid-cols-2 gap-3">
          {step.options.map((opt, k) => {
            const selected = (answers[step.field] || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => toggleMulti(step.field, opt.value)}
                className={`quiz-option-dark text-left flex items-start gap-3 slide-in ${selected ? 'selected' : ''}`}
                style={{ animationDelay: `${k * 50}ms` }}
                data-testid={`multi-${step.field}-${opt.value}`}
              >
                <span className="quiz-check-dark"><Check className="w-3.5 h-3.5" strokeWidth={3.5} /></span>
                <span className="flex-1">
                  <span className="font-bold text-white text-sm">{opt.label}</span>
                  {opt.desc && <span className="block text-purple-200/65 text-xs mt-0.5">{opt.desc}</span>}
                </span>
              </button>
            );
          })}
          {step.hint && (
            <div className="urg-box-dark sm:col-span-2 mt-2">
              <p className="leading-relaxed">{step.hint}</p>
            </div>
          )}
        </div>
      )}

      {step.type === 'sliders' && (
        <div className="space-y-6">
          {step.sliders.map((sl) => {
            const val = (answers.datos && answers.datos[sl.key]) ?? sl.def;
            const pct = ((val - sl.min) / (sl.max - sl.min)) * 100;
            return (
              <div key={sl.key} data-testid={`slider-${sl.key}`}>
                <div className="flex items-baseline justify-between mb-2.5">
                  <label className="font-bold text-white text-sm tracking-wider uppercase">{sl.label}</label>
                  <span className="font-display text-2xl font-bold text-fuchsia-300">
                    {val} <span className="text-sm text-purple-300/70 font-sans font-medium">{sl.unit}</span>
                  </span>
                </div>
                <input
                  type="range"
                  min={sl.min}
                  max={sl.max}
                  value={val}
                  onChange={(e) => setAnswer('datos', { ...answers.datos, [sl.key]: Number(e.target.value) })}
                  className="range-purple"
                  style={{ '--val': `${pct}%` }}
                />
              </div>
            );
          })}
          <div className="urg-box-dark"><p>{step.hint}</p></div>
        </div>
      )}

      {step.type === 'testimonial' && (
        <div className="space-y-4">
          <BeforeAfterCarousel />

          <div className="grid grid-cols-3 gap-2 text-center">
            {step.stats.map((s, i) => (
              <div key={i} className="glass-card-sm py-3 px-2">
                <p className="font-display text-lg sm:text-xl font-bold text-white">{s.v}</p>
                <p className="text-[10px] text-purple-300/70 mt-0.5 leading-tight">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {step.testimonials.map((t, i) => (
              <div key={i} className="glass-card-sm p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white font-bold flex items-center justify-center text-sm shadow-lg shadow-fuchsia-500/40">{t.ini}</div>
                  <div>
                    <p className="font-bold text-white text-sm">{t.name}</p>
                    <div className="flex text-fuchsia-300 text-xs gap-0.5">
                      {[...Array(5)].map((_, k) => <Star key={k} className="w-3 h-3 fill-current" />)}
                    </div>
                  </div>
                </div>
                <p className="text-purple-100/80 text-sm italic">"{t.text}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {step.type === 'insight' && (
        <div className="space-y-5">
          {step.intro && (
            <p
              className="text-purple-100/90 text-sm sm:text-base leading-relaxed -mt-2"
              dangerouslySetInnerHTML={{ __html: step.intro }}
            />
          )}
          {step.technologies && (
            <div className="space-y-2.5">
              {step.technologies.map((t, i) => (
                <div
                  key={t.name}
                  className="quiz-option-dark !cursor-default flex items-start gap-3 slide-in !py-3"
                  style={{ animationDelay: `${i * 70}ms` }}
                  data-testid={`tech-${i}`}
                >
                  <span className="text-xl flex-shrink-0 mt-0.5">{t.icon}</span>
                  <span className="flex-1">
                    <span className="block font-bold text-white text-sm sm:text-[15px]">{t.name}</span>
                    <span className="block text-purple-200/75 text-xs sm:text-[13px] mt-0.5 leading-snug">{t.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {step.body && (
            <p className="text-purple-100/80 text-sm leading-relaxed">{step.body}</p>
          )}
          {step.pills && (
            <div className="flex flex-wrap gap-2">
              {step.pills.map((p, i) => (
                <span key={i} className="chip-dark">{p}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {step.type === 'yesno' && (
        <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 border border-purple-400/30">
          <img src={RESULT_IMG} alt="Resultado" className="w-full h-full object-cover" style={{ filter: 'brightness(0.7) saturate(1) contrast(1.05)' }} />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-950/95 via-purple-950/40 to-fuchsia-900/30 flex flex-col justify-end p-5">
            <p className="pill-tag mb-2 w-fit"><Sparkles className="w-3 h-3" />TRANSFORMACIÓN REAL</p>
            <p className="text-white font-display text-xl sm:text-2xl font-bold leading-tight">"Resultados visibles desde<br />las primeras sesiones"</p>
            <p className="text-purple-200/80 text-xs mt-1">Caso documentado · Mistika</p>
          </div>
        </div>
      )}

      {step.type === 'booking' && (
        <BookingStep answers={answers} onSuccess={onBookingSuccess} />
      )}

      {step.type === 'contact' && (
        <div className="space-y-3">
          <div className="urg-box-dark mb-3">
            <p className="font-bold text-white mb-1">Tu plan incluirá:</p>
            <p className="leading-relaxed text-xs">
              ✓ Análisis corporal con equipos especializados<br />
              ✓ Protocolo personalizado para tu cuerpo<br />
              ✓ Cronograma de sesiones y resultados esperados<br />
              ✓ Opciones de inversión sin compromiso
            </p>
          </div>
          <input className="purple-input-dark" placeholder="Tu nombre completo" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name-input" />
          <input className="purple-input-dark" type="tel" placeholder="WhatsApp (ej: 311 234 5678)" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone-input" />
          <input className="purple-input-dark" type="email" placeholder="Correo electrónico (opcional)" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email-input" />
          <div className="flex items-start gap-2 text-purple-300/65 text-xs">
            <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>No compartimos tu información. Solo te contactamos para confirmar tu cita gratuita.</p>
          </div>
        </div>
      )}
    </div>
  );
}
